import { Receipt, SpinnerGap, ArrowRight, Warning } from "@phosphor-icons/react";

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(v || 0);

/**
 * Sticky totals card on the right side of the checkout.
 * Displays subtotal, discounts, shipping, tax and total — all values
 * fed by the latest /checkout/verify response.
 */
export default function CheckoutTotalsCard({
  verifying, data, currency = "EUR",
  canSubmit, submitting, submitError,
  onSubmit,
}) {
  // `data` is whatever the /checkout/verify endpoint echoed back.
  // The legacy response wraps everything under `checkoutData`, the
  // direct proxy doesn't — we accept both shapes.
  const d = data?.checkoutData || data || {};
  const subtotal = Number(d.SubTotal ?? d.Subtotal ?? 0);
  const totalPortes = Number(d.TotalPortes ?? d.Portes ?? 0);
  const totalDescontos = Number(d.TotalDescontos ?? d.Descontos ?? 0);
  const totalIva = Number(d.TotalIva ?? d.Iva ?? 0);
  const total = Number(d.Total ?? 0);
  const errMsg = d.MensagemErro || d.message;

  return (
    <aside className="lg:sticky lg:top-6 h-fit space-y-4" data-testid="checkout-totals">
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm">
        <h2 className="text-base font-black tracking-tighter text-[#0F1829] flex items-center gap-2 mb-4" style={{ fontFamily: "'Fraunces', sans-serif" }}>
          <Receipt size={18} className="text-[#002480]" />
          Resumo da Encomenda
          {verifying && <SpinnerGap size={14} className="animate-spin text-[#6B7280] ml-auto" />}
        </h2>

        <div className="space-y-2 text-sm">
          <Row label="Subtotal" value={fmt(subtotal, currency)} />
          {totalDescontos > 0 && <Row label="Descontos" value={`- ${fmt(totalDescontos, currency)}`} negative />}
          {totalPortes > 0 && <Row label="Portes" value={fmt(totalPortes, currency)} />}
          {totalIva > 0 && <Row label="IVA" value={fmt(totalIva, currency)} />}
          <div className="pt-3 border-t border-[#E5E7EB] flex justify-between items-baseline">
            <span className="text-[#6B7280]">Total</span>
            <span className="text-xl font-bold text-[#0F1829]" data-testid="checkout-total">{fmt(total, currency)}</span>
          </div>
        </div>

        {errMsg && (
          <div className="mt-4 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2"
            data-testid="checkout-warning">
            <Warning size={14} className="mt-0.5 shrink-0" />
            <span>{errMsg}</span>
          </div>
        )}
        {submitError && (
          <div className="mt-4 p-3 rounded-md bg-red-50 border-2 border-[#DC2626] text-[#DC2626] text-xs font-bold flex items-start gap-2 shadow-sm"
            role="alert" aria-live="assertive" data-testid="checkout-submit-error">
            <span className="w-4 h-4 rounded-full bg-[#DC2626] text-white text-[10px] grid place-items-center shrink-0">!</span>
            <span>{submitError}</span>
          </div>
        )}

        <button type="button" onClick={onSubmit}
          disabled={!canSubmit || submitting}
          data-testid="checkout-next-btn"
          className="mt-5 w-full py-3 bg-[#0F1829] hover:bg-[#002480] text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]">
          {submitting ? <SpinnerGap size={16} className="animate-spin" /> : null}
          Avançar para pagamento
          <ArrowRight size={16} />
        </button>
      </div>

      <p className="text-[11px] text-[#9CA3AF] text-center px-2">
        Ao continuar, o total será confirmado na escolha do método de pagamento.
      </p>
    </aside>
  );
}

function Row({ label, value, negative = false }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#6B7280]">{label}</span>
      <span className={negative ? "text-[#059669] font-medium" : "text-[#0F1829] font-medium"}>{value}</span>
    </div>
  );
}
